import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";
import { Box } from "@mui/material";
import { isEmpty } from "lodash";

import { AlertModal, ButtonComp, Icon } from "components";
import { getArtieLogo, getIsMaximized, selectPropertyId } from "slices";
import { addNewMessage, removeAndAddMessages, updateFaq, updateFeedbackMessages } from "slices/messages-slice/index";
import { formatMessageEdit, sendHayleyMessage } from "utils/methods/index";
import { resetLabelItems } from "slices/param-slice/index";
import { setReplies } from "slices/replies-slice/index";
import { renderMascot, saveHayleyChatbotFeedback } from "utils";

const FeedbackForm = ({ isPositive, isLastBot, staticImageData, isUserMsg, position, mascotSettings, customSize }) => {
  const dispatch = useDispatch();

  const propertyId = useSelector(selectPropertyId);
  const artieLogo = useSelector(getArtieLogo);
  const isMaximized = useSelector(getIsMaximized);
  const [comment, setComment] = useState("");
  const [feedbackLoader, setFeedbackLoader] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const ratings = [t("excellent"), t("needImprovement")];
  const [rating, setRating] = useState(isPositive ? t("excellent") : t("needImprovement"));

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    setComment("");
  };

  const submitFeedback = () => {
    setFeedbackLoader(true);

    const eventHash = {
      event_name: "h_click",
      event_params: {
        h_page_type: "hayley",
        h_clicked_location: "submit_feedback",
        h_stream: "hyly",
        h_display_method: "iframe",
      },
    };
    window.parent.postMessage(JSON.stringify(eventHash), "*");

    const rate = rating === t("excellent") ? "thumbs_up" : "thumbs_down";

    const payload = {
      message: comment,
      feedback: rate,
      propertyId,
    };

    saveHayleyChatbotFeedback(payload)
      .then(() => {
        setShowForm(false);
        setFeedbackLoader(false);
        setShowSuccess(true);

        dispatch(
          removeAndAddMessages([
            { message: rating === t("excellent") ? t("chatbotLikeQuestion") : t("chatbotExpQuestion"), speaker: "bot", createdAt: new Date().toISOString() },
          ])
        );

        sendHayleyMessage({
          text: `${t(rating)} <br /> ${comment}` || "",
          queryType: "question",
          ownerType: "prospect",
          kgNodeValue: `Feedback`,
        });

        dispatch(
          addNewMessage([
            {
              message: `${rating} <br /> ${comment}`,
              speaker: "user",
              createdAt: new Date().toISOString(),
            },
          ])
        );

        const nex = [
          {
            message: payload?.feedback_type === "positive_feedback" ? "whatNext" : "howHelp",
            speaker: "bot",
            createdAt: new Date().toISOString(),
          },
        ];

        dispatch(addNewMessage(nex));

        sendHayleyMessage({
          text: t(payload?.feedback_type === "positive_feedback" ? "whatNext" : "howHelp") || "",
          queryType: "answer",
          ownerType: "agent",
          kgNodeValue: t(payload?.feedback_type === "positive_feedback" ? "whatNext" : "howHelp") || "",
        });

        const reply = {
          title: "",
          options: [],
          optionLabel: "",
          optionsType: "Init",
          checkStorage: false,
          disableResetBox: true,
          renderType: "boxView",
        };

        dispatch(setReplies(reply));

        dispatch(updateFeedbackMessages());

        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      })
      .catch((e) => {
        console.error(e);
        setShowForm(false);
      });
  };

  const handleCancel = () => {
    sendHayleyMessage({
      text: "Cancel",
      queryType: "question",
      ownerType: "prospect",
      kgNodeValue: "Feedback",
    });

    setShowForm(false);

    dispatch(
      removeAndAddMessages([
        { message: rating === t("excellent") ? t("chatbotLikeQuestion") : t("chatbotExpQuestion"), speaker: "bot", createdAt: new Date().toISOString() },
      ])
    );

    dispatch(addNewMessage([{ message: "cancel", speaker: "user", createdAt: new Date().toISOString() }]));
    formatMessageEdit("Init", null, "feedbackCancel");
    dispatch(updateFaq(false));
    dispatch(resetLabelItems());

    dispatch(updateFeedbackMessages());
  };

  return (
    <>
      {showForm && (
        <div className="hayley-mt-4">
          <div style={{ overflowWrap: "break-word" }}>
            <div className={`hayley-flex ${isLastBot && (isMaximized ? "hayley-mb-4" : "hayley-mb-3")}`}>
              {isUserMsg ? (
                isMaximized ? (
                  <Icon iconType="DefaultUser" classes={"hayley-h-9 hayley-w-9 hayley-object-cover"} />
                ) : (
                  ""
                )
              ) : (
                <>
                  {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

                  {!isEmpty(artieLogo) && (
                    <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                      <img
                        id="staticImage"
                        src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                        alt=""
                        className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                      />
                    </div>
                  )}
                </>
              )}
              <div className="hayley-mx-3">
                <div className="hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary">
                  <Box>{rating === t("excellent") ? t("chatbotLikeQuestion") : t("chatbotExpQuestion")}</Box>
                </div>
                <Box className="hayley-my-3 hayley-flex hayley-flex-wrap">
                  {ratings.map((v, i) => (
                    <ButtonComp
                      testId="feedbackBtn"
                      key={i}
                      selected={v === rating}
                      icon={v === t("excellent") ? "like" : "dislike"}
                      label={v}
                      onClick={() => handleRatingChange(v)}
                      disabled={false}
                      className="!hayley-mb-2 !hayley-mr-2 !hayley-px-5 !hayley-py-2"
                      highlightSelected={true}
                    />
                  ))}
                </Box>
                <textarea
                  className="hayley-h-32 hayley-w-full hayley-border hayley-border-gray-300 hayley-p-4 hayley-font-sans hayley-text-sm hayley-placeholder-gray-500 hayley-outline-none focus:hayley-border-gray-300"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={rating === t("excellent") ? t("whatULike") : t("whatNeedToImprove")}
                ></textarea>
                <div className="hayley-mt-3 hayley-flex hayley-items-center hayley-gap-2">
                  <ButtonComp
                    testId="submitFeedback"
                    filled
                    label={feedbackLoader ? t("submitting") : t("submitFeedback")}
                    onClick={submitFeedback}
                    disabled={feedbackLoader || comment.trim() === ""}
                  />
                  <ButtonComp testId="cancelFeedback" label={t("cancel")} onClick={handleCancel} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSuccess && (
        <AlertModal icon="DoubleCheckIcon" onClose={() => setShowForm(false)} className={"hayley-font-semibold"}>
          <span>{t("thanksFeedback")}</span>
        </AlertModal>
      )}
    </>
  );
};

export default FeedbackForm;
